import { createPlugin } from '@customerio/dryad';
import { ConditionType } from './condition-types';
import { conditionToNamedBitmap, decodeMessageBitmapNode } from './named-bitmap';
import type { NamedBitmapFilter, NotFilter, SerializedLegacyMessageFilter } from './wire-types';
import {
  isMessageEventType,
  toPickerMessageName,
  toWireMessageName,
  validateMessageFilter,
  type MessageFilterConditionState,
} from './message-filter-condition';

// The proto message leaf: `{ named_bitmap: { type: 3, name: <base64> } }`,
// negation as a `{ not: ... }` wrapper. The base64 name encodes the whole
// condition — event type prefix, message id/name, `_h` human suffix — in the
// message-value grammar owned by the named-bitmap codec.
//
// The `named_bitmap` leaf key is shared with the opt-out condition, which uses
// bitmap types 22/33 — this plugin claims type 3 only. A decoded event type
// outside the message metrics (a behavioral `event`/`page` bitmap) is also
// declined; those belong to the event plugin, which gates on the disjoint
// behavioral types.

/**
 * Decodes the node into state, or null when this plugin shouldn't claim it:
 * wrong bitmap type, an undecodable name (bad base64, unknown type prefix), or
 * a decoded event type the message row can't edit.
 */
function decodeMessageBitmap(json: unknown): MessageFilterConditionState | null {
  const condition = decodeMessageBitmapNode(json);
  if (condition == null || !isMessageEventType(condition.event.type)) {
    return null;
  }
  return {
    eventName: toPickerMessageName(condition.event.name),
    eventType: condition.event.type,
    human: condition.event.human === true,
    inverse: condition.inverse,
  };
}

/**
 * `conditionToNamedBitmap` re-derives the base64 name and emits its own
 * `{ not }` wrapper from `inverse`.
 */
function serializeMessageBitmap(state: MessageFilterConditionState): NamedBitmapFilter | NotFilter {
  const event: SerializedLegacyMessageFilter['event'] = {
    name: toWireMessageName(state.eventName),
    type: state.eventType,
    filters: {},
  };
  if (state.human) {
    event.human = true;
  }
  const condition: SerializedLegacyMessageFilter = {
    type: 'message',
    event,
    times: 1,
    within: 0,
    inverse: state.inverse,
  };
  return conditionToNamedBitmap(condition);
}

export const peopleFilterMessagePlugin = createPlugin({
  type: ConditionType.Message,
  label: 'Message',
  parse: decodeMessageBitmap,
  serialize: serializeMessageBitmap,
  validate: validateMessageFilter,
  createDefault: (): MessageFilterConditionState => ({
    eventName: '',
    eventType: 'sent_email',
    human: false,
    inverse: false,
  }),
});
